import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * HelpTooltip - A reusable tooltip component with help icon
 * @param {string} text - The help text to display
 * @param {string} position - Tooltip position: 'top', 'bottom', 'left', 'right' (default: 'top')
 */
const HelpTooltip = ({ text, position = 'top' }) => {
    const [isVisible, setIsVisible] = useState(false);

    if (!text) return null;

    const positionStyles = {
        top: {
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '8px'
        },
        bottom: {
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '8px'
        },
        left: {
            right: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            marginRight: '8px'
        },
        right: {
            left: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            marginLeft: '8px'
        }
    };

    return (
        <div
            style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                marginLeft: '0.35rem'
            }}
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
        >
            {/* Help Icon */}
            <div
                style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: 'rgba(148, 163, 184, 0.2)',
                    border: '1px solid rgba(148, 163, 184, 0.4)',
                    color: '#94a3b8',
                    fontSize: '11px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'help',
                    transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.6)';
                    e.currentTarget.style.color = '#60a5fa';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(148, 163, 184, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.4)';
                    e.currentTarget.style.color = '#94a3b8';
                }}
            >
                ?
            </div>

            {/* Tooltip */}
            <AnimatePresence>
                {isVisible && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        style={{
                            position: 'absolute',
                            ...positionStyles[position],
                            background: 'rgba(15, 23, 42, 0.98)',
                            backdropFilter: 'blur(12px)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            borderRadius: '8px',
                            padding: '0.75rem 1rem',
                            maxWidth: '320px',
                            minWidth: '200px',
                            width: 'max-content',
                            fontSize: '0.85rem',
                            lineHeight: '1.5',
                            color: '#e2e8f0',
                            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
                            zIndex: 1000,
                            pointerEvents: 'none',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                        }}
                    >
                        {/* Arrow */}
                        <div
                            style={{
                                position: 'absolute',
                                width: '8px',
                                height: '8px',
                                background: 'rgba(15, 23, 42, 0.98)',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                transform: 'rotate(45deg)',
                                ...(position === 'top' && {
                                    bottom: '-5px',
                                    left: '50%',
                                    marginLeft: '-4px',
                                    borderTop: 'none',
                                    borderLeft: 'none'
                                }),
                                ...(position === 'bottom' && {
                                    top: '-5px',
                                    left: '50%',
                                    marginLeft: '-4px',
                                    borderBottom: 'none',
                                    borderRight: 'none'
                                }),
                                ...(position === 'left' && {
                                    right: '-5px',
                                    top: '50%',
                                    marginTop: '-4px',
                                    borderTop: 'none',
                                    borderRight: 'none'
                                }),
                                ...(position === 'right' && {
                                    left: '-5px',
                                    top: '50%',
                                    marginTop: '-4px',
                                    borderBottom: 'none',
                                    borderLeft: 'none'
                                })
                            }}
                        />
                        <span style={{ display: 'block' }}>{text}</span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default HelpTooltip;
